/**
 * Resolve the backend agent ID used by the "Discuss this Execution" modal.
 *
 * The Discuss feature opens a chat against a pre-registered debugging agent
 * (typically `automation_debugger` in the agents service). The agent must
 * accept a `<execution_context>...</execution_context>` envelope in the first
 * user turn — see `buildDiscussSystemMessage.ts` for the markdown shape.
 *
 * Why an indirection (env var + fallback) rather than a hard-coded id:
 *
 * - The backend may rename the debugger or stand up environment-specific
 *   variants (e.g. `automation_debugger_dev`) without forcing a frontend
 *   release.
 * - Local development against a stub agent service is easier when this is
 *   override-able from `.env.local`.
 *
 * The fallback constant is the production agent id and is the source of
 * truth — `.env.example` documents the override path but the deployed app
 * works without any env var set.
 *
 * Set `VITE_DISCUSS_AGENT_ID` in `apps/stream/.env.local` to override.
 */

/**
 * Hard-coded fallback agent id. Kept here (rather than in `.env.example`
 * only) so the frontend always has a deterministic target even if the env
 * var is unset in CI or production builds.
 */
const DEFAULT_DISCUSS_AGENT_ID = 'automation_debugger'

export function getDiscussAgentId(): string {
  // Vite inlines `import.meta.env.*` at build time, so this is a static
  // string substitution at runtime — no per-call cost.
  const fromEnv = import.meta.env?.VITE_DISCUSS_AGENT_ID
  if (typeof fromEnv === 'string' && fromEnv.trim().length > 0) {
    return fromEnv.trim()
  }
  return DEFAULT_DISCUSS_AGENT_ID
}
